import React, { useState, useEffect, useContext } from 'react'
import Form from 'react-bootstrap/Form'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import {
  useCreateDashboardColumn,
  useDeleteDashboardColumn
} from 'api/hooks/use-dashboard-columns'
import { useQueryClient } from 'react-query'
import UserContext from 'context/user-context'
import { isColumnFiltered, getColumnDisplayName } from './utils'

function UpdateColumn({
  column,
  dashboardId,
  currentColumns,
  disabled,
  filterParams,
  // Passed down rather than resolved here: this renders once per column, and the answer is
  // the same for all of them.
  groupingName = null
}) {
  const queryClient = useQueryClient()
  const { municipality } = useContext(UserContext)

  const [isChecked, setIsChecked] = useState(() =>
    currentColumns.map((c) => c.id).includes(column.id)
  )

  // Update checked state when currentColumns changes (e.g., from group selection)
  useEffect(() => {
    setIsChecked(currentColumns.map((c) => c.id).includes(column.id))
  }, [currentColumns, column.id])
  const options = {
    onSuccess: () => {
      queryClient.invalidateQueries(`dashboards/${dashboardId}`)
    }
  }
  const params = {
    dashboard_id: dashboardId,
    table_column_id: column.id
  }

  const { mutate: addColumn } = useCreateDashboardColumn({ params, options })
  const { mutate: deleteColumn } = useDeleteDashboardColumn({
    id: currentColumns.find((c) => c.id === column.id)?.dashboard_column_id,
    options
  })

  // Check if this column has active filters
  const isFiltered = isColumnFiltered(column, filterParams)

  const handleToggle = (e) => {
    // Prevent unchecking if the column is filtered
    if (!e.target.checked && isFiltered) {
      e.preventDefault()
      return
    }

    setIsChecked(e.target.checked)
    if (e.target.checked) {
      addColumn()
    } else {
      deleteColumn()
    }
  }
  // See the note in transactions/table/header.jsx: one shared resolver, so the
  // rollup columns get the municipality's segment name plus the grouping they bucket by,
  // which is what tells two otherwise identically-named rollups apart.
  const columnName = (column) =>
    getColumnDisplayName(
      { ...column, name: column.display_name },
      municipality,
      groupingName
    )

  return (
    <div key={`check-${column.id}`} className="mt-2 d-flex align-items-center">
      {isFiltered && isChecked ? (
        <>
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip>
                This column cannot be removed because it has active filters
                applied
              </Tooltip>
            }
          >
            <div>
              <Form.Check
                disabled={disabled || (isChecked && isFiltered)}
                type="checkbox"
                label={columnName(column)}
                id={column.id}
                onChange={handleToggle}
                checked={isChecked}
              />
            </div>
          </OverlayTrigger>
        </>
      ) : (
        <Form.Check
          disabled={disabled || (isChecked && isFiltered)}
          type="checkbox"
          label={columnName(column)}
          id={column.id}
          onChange={handleToggle}
          checked={isChecked}
        />
      )}
    </div>
  )
}

export default UpdateColumn
